import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  ChevronDown,
  ChevronUp,
  Crown,
  HelpCircle,
  Mail,
  Map,
  MessageSquare,
  Shield,
  Timer
} from 'lucide-react';
import LegalLayout from '../LegalLayout';

const FAQItem = ({ question, answer, isOpen, onToggle }) => {
  const accentColor = '#0ea5e9';

  return (
    <div
      className="rounded-xl overflow-hidden transition-all duration-300"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)'
      }}
    >
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between text-left transition-colors"
        style={{ color: 'var(--text-primary)' }}
      >
        <span className="font-medium pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 flex-shrink-0" style={{ color: accentColor }} />
        ) : (
          <ChevronDown className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--text-tertiary)' }} />
        )}
      </button>
      {isOpen && (
        <div
          className="px-6 pb-4"
          style={{ color: 'var(--text-secondary)' }}
        >
          {answer}
        </div>
      )}
    </div>
  );
};

const Support = () => {
  const accentColor = '#0ea5e9';
  const [openFAQ, setOpenFAQ] = useState(0);

  const faqs = [
    {
      icon: <Timer className="w-5 h-5" />,
      question: "How does a drift work?",
      answer: (
        <div className="space-y-2">
          <p>Pick how long you want to be gone — 15 minutes, 30 minutes, 1 hour, 2 hours, or a half day — and start. Drift deals you a small real-world mission, shows a quiet breathing circle and countdown, and waits for the single button press that says you are back.</p>
        </div>
      )
    },
    {
      icon: <Map className="w-5 h-5" />,
      question: "What kinds of missions will I get?",
      answer: (
        <div className="space-y-2">
          <p>Small, real-world prompts like "Walk a street you have never taken", "Call someone you have been meaning to call", or "Sit somewhere busy and just watch" — drawn from categories like Explore, Connect, and Observe.</p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-tertiary)' }}>
            Missions are suggestions. Skip or reroll any that do not fit your moment.
          </p>
        </div>
      )
    },
    {
      icon: <BookOpen className="w-5 h-5" />,
      question: "What happens when I come back?",
      answer: (
        <div className="space-y-2">
          <p>Write a short reflection and tag the mood — calm, energized, grateful, reflective, joyful, peaceful, inspired, or grounded. Skip it whenever you like. Every completed drift lands in your Journal, grouped by day.</p>
        </div>
      )
    },
    {
      icon: <Shield className="w-5 h-5" />,
      question: "Is my journal private?",
      answer: (
        <div className="space-y-2">
          <p>Completely. Reflections, moods, streaks, and your calendar heatmap all live on your device only. Drift has no account, no analytics, and makes no network requests.</p>
        </div>
      )
    },
    {
      icon: <Crown className="w-5 h-5" />,
      question: "What is Drift Pro and how do I restore it?",
      answer: (
        <div className="space-y-2">
          <p>Drift Pro is an optional upgrade available as a monthly or yearly subscription, or a one-time lifetime unlock. To restore a previous purchase:</p>
          <ol className="list-decimal pl-5 space-y-1">
            <li>Open Settings inside Drift</li>
            <li>Tap Restore Purchases</li>
            <li>Sign in with the Apple ID you used to purchase</li>
          </ol>
        </div>
      )
    }
  ];

  return (
    <LegalLayout app="Drift" docType="Support" icon={HelpCircle}>
        {/* Contact Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <a
            href="mailto:support@feraltechlabs.co.in"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <Mail className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Email Support
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  support@feraltechlabs.co.in
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              We typically respond within 24-48 hours
            </p>
          </a>

          <a
            href="mailto:support@feraltechlabs.co.in?subject=Drift%20Bug%20Report"
            className="block p-6 rounded-xl transition-all duration-300"
            style={{
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)'
            }}
            onMouseOver={e => e.currentTarget.style.borderColor = accentColor}
            onMouseOut={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
          >
            <div className="flex items-center gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: `${accentColor}20` }}
              >
                <MessageSquare className="w-6 h-6" style={{ color: accentColor }} />
              </div>
              <div>
                <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>
                  Report a Bug
                </h3>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  Help us improve Drift
                </p>
              </div>
            </div>
            <p className="mt-4 text-sm" style={{ color: 'var(--text-tertiary)' }}>
              Include your iOS version and steps to reproduce
            </p>
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
              />
            ))}
          </div>
        </div>

        {/* App Info */}
        <div
          className="rounded-xl p-6"
          style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-color)'
          }}
        >
          <h3 className="font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            App Information
          </h3>
          <div className="grid sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>App Name:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Drift</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Developer:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Feral Techlabs</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Platform:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>iOS 17+</span>
            </div>
            <div>
              <span style={{ color: 'var(--text-tertiary)' }}>Category:</span>
              <span className="ml-2" style={{ color: 'var(--text-secondary)' }}>Lifestyle</span>
            </div>
          </div>
        </div>

        {/* Legal Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link
            to="/apps/drift/privacy"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Privacy Policy
          </Link>
          <span style={{ color: 'var(--text-tertiary)' }}>•</span>
          <Link
            to="/apps/drift/terms"
            className="text-sm transition-colors"
            style={{ color: 'var(--text-tertiary)' }}
            onMouseOver={e => e.currentTarget.style.color = accentColor}
            onMouseOut={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          >
            Terms &amp; Conditions
          </Link>
        </div>
    </LegalLayout>
  );
};

export default Support;
